import fleetAnalyticsService from "@/services/mrfiktiv/services/fleet/analyticsFleetService";
import {
  MrfiktivBaseChartItemListViewmodelGen,
  MrfiktivBaseChartItemViewmodelGen,
  MrfiktivInvoiceChartItemListViewModelGen,
  MrfiktivInvoiceChartItemViewModelGen,
  MrfiktivReportWeeklyAnalysisPerPartnerItemGen,
  MrfiktivReportWeeklyAnalysisPerPartnerViewmodelGen,
  MrfiktivStatusDistributionAnalyticsItemGen,
  MrfiktivStatusDistributionAnalyticsViewModelGen
} from "@/services/mrfiktiv/v1/data-contracts";
import store from "@/store/VuexPlugin";
import { Action, getModule, Module, Mutation, VuexModule } from "vuex-module-decorators";

@Module({
  dynamic: true,
  namespaced: true,
  name: "analyticsFleetStore",
  store
})
export class AnalyticsFleetStore extends VuexModule {
  private _dateRange: string[] = [
    new Date().toISOString(),
    new Date(new Date().setDate(new Date().getDate() - 365)).toISOString()
  ];

  get dateRange(): string[] {
    return this._dateRange;
  }

  get from(): string {
    const dates: number[] = [];
    for (const date of this.dateRange) {
      dates.push(new Date(date).getTime());
    }
    const tempo = new Date(new Date(Math.min.apply(null, dates)).setUTCHours(0, 0, 0, 0)).toISOString();
    return tempo;
  }

  get to(): string {
    const dates: number[] = [];
    for (const date of this.dateRange) {
      dates.push(new Date(date).getTime());
    }
    const tempo = new Date(new Date(Math.max.apply(null, dates)).setUTCHours(20, 59, 59, 999)).toISOString();
    return tempo;
  }

  @Mutation
  _mutateDateRange(dateRange: string[]) {
    this._dateRange = dateRange;
  }

  @Action
  setDateRange(dateRange: string[]) {
    this.context.commit("_mutateDateRange", dateRange);
  }

  private _damageTypeSubtotalData: MrfiktivInvoiceChartItemViewModelGen[] = [];

  get damageTypeSubtotalOutboundData(): MrfiktivInvoiceChartItemViewModelGen[] {
    return this._damageTypeSubtotalData;
  }

  @Action
  async getDamageTypeSubtotalData(data: { partnerId: string; fleetId: string }) {
    const result: MrfiktivInvoiceChartItemListViewModelGen = await fleetAnalyticsService.getTotalSubtotalPerDamageType(
      data.partnerId,
      data.fleetId,
      this.from,
      this.to
    );

    this.context.commit("_mutateDamageTypeSubtotalData", result.data);
  }

  @Mutation
  _mutateDamageTypeSubtotalData(data: MrfiktivInvoiceChartItemViewModelGen[]) {
    this._damageTypeSubtotalData = data;
  }

  private _coverageTypeSubtotalData: MrfiktivInvoiceChartItemViewModelGen[] = [];

  get coverageTypeSubtotalOutboundData(): MrfiktivInvoiceChartItemViewModelGen[] {
    return this._coverageTypeSubtotalData;
  }

  @Action
  async getCoverageTypeSubtotalData(data: { partnerId: string; fleetId: string }) {
    const result: MrfiktivInvoiceChartItemListViewModelGen = await fleetAnalyticsService.getTotalSubtotalPerCoverageType(
      data.partnerId,
      data.fleetId,
      this.from,
      this.to
    );

    this.context.commit("_mutateCoverageTypeSubtotalData", result.data);
  }

  @Mutation
  _mutateCoverageTypeSubtotalData(data: MrfiktivInvoiceChartItemViewModelGen[]) {
    this._coverageTypeSubtotalData = data;
  }

  private _tripTypeData: MrfiktivBaseChartItemViewmodelGen[] = [];

  get tripTypeData(): MrfiktivBaseChartItemViewmodelGen[] {
    return this._tripTypeData;
  }

  @Action
  async getTripTypeData(data: { partnerId: string; fleetId: string }) {
    const result: MrfiktivBaseChartItemListViewmodelGen = await fleetAnalyticsService.getCountPerTripType(
      data.partnerId,
      data.fleetId,
      this.from,
      this.to
    );

    this.context.commit("_mutateTripTypeData", result.data);
  }

  @Mutation
  _mutateTripTypeData(data: MrfiktivBaseChartItemViewmodelGen[]) {
    this._tripTypeData = data;
  }

  private _damageTypeData: MrfiktivBaseChartItemViewmodelGen[] = [];

  get damageTypeData(): MrfiktivBaseChartItemViewmodelGen[] {
    return this._damageTypeData;
  }

  @Action
  async getDamageTypeData(data: { partnerId: string; fleetId: string }) {
    const result: MrfiktivBaseChartItemListViewmodelGen = await fleetAnalyticsService.getCountPerDamageType(
      data.partnerId,
      data.fleetId,
      this.from,
      this.to
    );

    this.context.commit("_mutateDamageTypeData", result.data);
  }

  @Mutation
  _mutateDamageTypeData(data: MrfiktivBaseChartItemViewmodelGen[]) {
    this._damageTypeData = data;
  }

  private _coverageTypeData: MrfiktivBaseChartItemViewmodelGen[] = [];

  get coverageTypeData(): MrfiktivBaseChartItemViewmodelGen[] {
    return this._coverageTypeData;
  }

  @Action
  async getCoverageTypeData(data: { partnerId: string; fleetId: string }) {
    const result: MrfiktivBaseChartItemListViewmodelGen = await fleetAnalyticsService.getCountPerCoverageType(
      data.partnerId,
      data.fleetId,
      this.from,
      this.to
    );

    this.context.commit("_mutateCoverageTypeData", result.data);
  }

  @Mutation
  _mutateCoverageTypeData(data: MrfiktivBaseChartItemViewmodelGen[]) {
    this._coverageTypeData = data;
  }

  private _statusDistributionData: MrfiktivStatusDistributionAnalyticsItemGen[] = [];

  get statusDistributionData(): MrfiktivStatusDistributionAnalyticsItemGen[] {
    return this._statusDistributionData;
  }

  @Action
  async getStatusDistribution(data: { partnerId: string; fleetId: string }) {
    const result: MrfiktivStatusDistributionAnalyticsViewModelGen = await fleetAnalyticsService.getStatusDistribution(
      data.partnerId,
      data.fleetId,
      this.from,
      this.to
    );

    this.context.commit("_mutateStatusDistributionData", result.data);
  }

  @Mutation
  _mutateStatusDistributionData(data: MrfiktivStatusDistributionAnalyticsItemGen[]) {
    this._statusDistributionData = data;
  }

  private _weekDayDistributionData: MrfiktivReportWeeklyAnalysisPerPartnerItemGen[] = [];

  get weekdayDistributionData(): MrfiktivReportWeeklyAnalysisPerPartnerItemGen[] {
    return this._weekDayDistributionData;
  }

  @Action
  async getWeekdayAnalysis(data: { partnerId: string; fleetId: string }) {
    const result: MrfiktivReportWeeklyAnalysisPerPartnerViewmodelGen = await fleetAnalyticsService.getWeekdayAnalysis(
      data.partnerId,
      data.fleetId,
      this.from,
      this.to
    );

    this.context.commit("_mutateWeekDayData", result.data);
  }

  @Mutation
  _mutateWeekDayData(data: MrfiktivReportWeeklyAnalysisPerPartnerItemGen[]) {
    this._weekDayDistributionData = data;
  }

  private _damageTypeAverageData: MrfiktivBaseChartItemViewmodelGen[] = [];

  get damageTypeAverageData(): MrfiktivBaseChartItemViewmodelGen[] {
    return this._damageTypeAverageData;
  }

  @Action
  async getDamageTypeAverageData(data: { partnerId: string; fleetId: string }) {
    const result: MrfiktivBaseChartItemListViewmodelGen = await fleetAnalyticsService.getDamageTypeAverage(
      data.partnerId,
      data.fleetId,
      this.from,
      this.to
    );

    this.context.commit("_mutateDamageTypeAverageData", result.data);
  }

  @Mutation
  _mutateDamageTypeAverageData(data: MrfiktivBaseChartItemViewmodelGen[]) {
    this._damageTypeAverageData = data;
  }

  private _topCreditorData: MrfiktivInvoiceChartItemListViewModelGen[] = [];

  get topCreditorData(): MrfiktivInvoiceChartItemListViewModelGen[] {
    return this._topCreditorData;
  }

  @Action
  async getTopCreditorData(data: { partnerId: string; fleetId: string }) {
    const result: MrfiktivInvoiceChartItemListViewModelGen = await fleetAnalyticsService.getTotalSubtotalBySeller(
      data.partnerId,
      data.fleetId,
      this.from,
      this.to
    );

    this.context.commit("_mutateTopCreditorData", result.data);
  }

  @Mutation
  _mutateTopCreditorData(data: MrfiktivInvoiceChartItemListViewModelGen[]) {
    this._topCreditorData = data;
  }
}

export const AnalyticsFleetModule = getModule(AnalyticsFleetStore);
